import { breakpoints, vars } from '@etg/wings';
import { useProperty } from '@eti/providers';
import { css } from '@eti/styles';
import { PRICE } from '../../../../../constants/searchFiltersConstants';
import PriceSliderFilter from '../PriceSliderFilter';
import { RESULT_PAGE_VERSION_3 } from '../../../constants/resultConstants';
import TravelTime from '../TravelTime';
import DepartureArrivalTime from '../DepartureArrivalTime';
import StopoverTypes from '../StopoverTypes';
import { useResultPageContext } from '../../v3/contexts/ResultPageContext';
import type { NonNullableRoutesType } from '../../../models';
import NumberOfStops from './NumberOfStops';
import Airlines from './Airlines';
import CheckedBaggage from './CheckedBaggage';
import type { Filters } from './MobileFilters';

const fullWidthFilters = css`
  padding-block: 16px;

  &:not(:first-of-type) {
    border-top: 1px solid ${vars.colors.divider};
  }

  & header {
    border: none;
  }

  &:first-child header {
    padding-top: 0;
  }

  @media (min-width: ${breakpoints._768}) {
    flex: 0 0 100%;
    max-width: 100%;
    width: 100%;
  }
`;

interface FilterContentProps {
  onFilter: (filterOptions: { [key: string]: string[] | null | string }) => void;
  selectedFilters?: Filters;
}

const FilterContent = ({ onFilter, selectedFilters }: FilterContentProps) => {
  const { p } = useProperty();
  const { searchData } = useResultPageContext();
  const resultSetMetaData = searchData?.resultSetMetaData;
  const searchRoutes: NonNullableRoutesType[] = searchData?.routes ?? [];
  const searchFormStopoverEnabled = p('Searchform.Stopover.Enabled');
  const resultPageVersion = String(p('Result.Page.Version'));
  const isCheckedBaggageEnabled = p('Result.Filter.CheckedBaggage.Enabled');
  const isAirlinesEnabled = p('Result.Filter.Airlines.Enabled');
  const isStopoverEnabled =
    searchFormStopoverEnabled && resultPageVersion === RESULT_PAGE_VERSION_3;

  return (
    <>
      {isStopoverEnabled && (
        <StopoverTypes
          className={fullWidthFilters}
          isInitiallyExpanded
          isToggleable
          onFilter={onFilter}
          selectedFilters={selectedFilters}
        />
      )}

      {!isStopoverEnabled && (
        <NumberOfStops
          className={fullWidthFilters}
          isInitiallyExpanded={false}
          isToggleable={false}
          onFilter={onFilter}
          selectedFilters={selectedFilters}
        />
      )}

      {isCheckedBaggageEnabled && (
        <CheckedBaggage
          className={fullWidthFilters}
          isInitiallyExpanded={false}
          isToggleable={false}
          onFilter={onFilter}
          selectedFilters={selectedFilters}
        />
      )}

      <PriceSliderFilter
        className={fullWidthFilters}
        isInitiallyExpanded={false}
        isToggleable={false}
        onFilter={onFilter}
        range={resultSetMetaData?.priceRange}
        selectedValues={selectedFilters && selectedFilters[PRICE]}
      />

      {isAirlinesEnabled && (
        <Airlines
          className={fullWidthFilters}
          isInitiallyExpanded={false}
          isToggleable={false}
          marketingCarriers={resultSetMetaData?.marketingCarriers ?? []}
          onFilter={onFilter}
          selectedFilters={selectedFilters}
        />
      )}

      {searchRoutes.map((route, i) => (
        <DepartureArrivalTime
          className={fullWidthFilters}
          destinationName={route?.destination.cityName ?? ''}
          index={i}
          isInitiallyExpanded={false}
          isToggleable={false}
          key={`${route?.departureAt}-${route?.origin.cityName}-${route?.destination.cityName}`}
          onFilter={onFilter}
          originName={route?.origin.cityName ?? ''}
          selectedFilters={selectedFilters}
        />
      ))}
      <TravelTime
        className={fullWidthFilters}
        isInitiallyExpanded={false}
        isToggleable={false}
        onFilter={onFilter}
        selectedFilters={selectedFilters}
        travelTimeRange={resultSetMetaData?.travelTimeRange}
      />
    </>
  );
};

export default FilterContent;
